import { FormEvent, useMemo, useState } from "react";
import { Tooltip } from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";

import styles from "./Answer.module.css";

import { useLogin } from "../../../utils/authConfig";
import { getToken } from "../../../utils/authConfig";
import {
  ChatAppResponse,
  getCitationFilePath,
  Feedback,
  logUserFeedback,
} from "../../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import AnswerModeIcon from "./AnswerIcon";
import { AnswerMode } from "../../answer-modes/AnswerModeContainer/AnswerMode";

import {
  ErrorCircle24Regular,
  ArrowClockwise24Regular,
} from "@fluentui/react-icons";
import { useUseCase } from "../../../context/UseCaseContext/UseCaseContext";
import MarkdownRenderer from "./AnswerMarkdownRenderer";
import { ProcessStepList } from "./ProcessStepList";
import {
  PositiveFeedbackModal,
  NegativeFeedbackModal,
} from "../FeedbackModal/FeedbackModal";

/** Lightweight HTML entity decoder (replaces `he` package).
 *  NOTE: Must NOT be called in the render path during streaming — it creates
 *  a DOM element and parses the full growing string every tick, causing
 *  visible rendering stalls. Call it lazily (on copy / feedback submit) only.
 */
const decodeHtml = (html: string): string => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

type VisualizationMap = Record<
  string,
  { language: string; code: string; description: string; validated: boolean }
>;

/**
 * Replace <<viz:ID>> markers with either:
 * - The actual code block (if visualization data arrived)
 * - A loading placeholder (if still waiting)
 */
function injectVisualizations(
  text: string,
  visualizations?: VisualizationMap,
): string {
  return text.replace(/<<viz:(viz_[a-f0-9]+)>>/g, (match, vizId) => {
    if (!visualizations || !visualizations[vizId]) {
      // Visualization data hasn't arrived yet — show placeholder
      return `\n\n<div class="viz-pending" data-viz-id="${vizId}"><div class="viz-pending-spinner"></div><span>Generating visualization…</span></div>\n\n`;
    }

    const viz = visualizations[vizId];
    return `\n\n\`\`\`${viz.language}\n${viz.code}\n\`\`\`\n\n`;
  });
}

interface Props {
  answer: ChatAppResponse;
  isSelected?: boolean;
  isStreaming: boolean;
  onCitationClicked: (filePath: string) => void;
  onThoughtProcessClicked: () => void;
  onSupportingContentClicked: () => void;
  showFollowupQuestions?: boolean;
  answerMode: AnswerMode;
  onRegenerate: () => void;
  isLast: boolean;
  error?: string;
  onRetry?: () => void;
}

export const Answer = ({
  answer,
  isSelected,
  isStreaming,
  onCitationClicked,
  onThoughtProcessClicked,
  onSupportingContentClicked,
  answerMode,
  onRegenerate,
  isLast,
  error,
  onRetry,
}: Props) => {
  const initializeAnswerFeedback = (answer: ChatAppResponse) => {
    if (answer.feedback == undefined) return undefined;
    if (Object.values(Feedback).includes(answer.feedback))
      return answer.feedback;
    return Feedback.Neutral;
  };

  const { selectedUseCase } = useUseCase();
  const context = answer.choices[0].context;
  const processSteps = context?.process_steps;
  const visualizations = context?.visualizations as
    | VisualizationMap
    | undefined;

  const parsedAnswer = useMemo(
    () =>
      parseAnswerToHtml(
        answer,
        isStreaming,
        onCitationClicked,
        selectedUseCase,
      ),
    [answer, isStreaming, selectedUseCase],
  );

  // Inject visualizations AFTER parsing citations.
  // This is now the final content passed to the markdown renderer — no HTML
  // decoding step in the render path (see decodeHtml note above).
  const contentWithViz = useMemo(
    () => injectVisualizations(parsedAnswer.answerHtml, visualizations),
    [parsedAnswer.answerHtml, visualizations],
  );

  const [feedbackState, setFeedbackState] = useState(
    initializeAnswerFeedback(answer),
  );
  const [isNegativeFeedbackDialogOpen, setIsNegativeFeedbackDialogOpen] =
    useState(false);
  const [isPositiveFeedbackDialogOpen, setIsPositiveFeedbackDialogOpen] =
    useState(false);
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>(
    [],
  );
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [copied, setCopied] = useState(false);

  const client = useMsal().instance;

  if (!useLogin) {
    throw new Error("The Feedback component requires useLogin to be true");
  }

  // Lazy: only decode when the user actually copies or submits feedback.
  const getDecodedContent = () => decodeHtml(contentWithViz);

  const handleCopy = async () => {
    try {
      const decoded = getDecodedContent();
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([decoded], { type: "text/plain" }),
          "text/html": new Blob([decoded], { type: "text/html" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const onLikeResponseClicked = async () => {
    if (feedbackState == Feedback.Positive) {
      setFeedbackState(Feedback.Neutral);
    } else {
      setFeedbackState(Feedback.Positive);
      setIsPositiveFeedbackDialogOpen(true);
    }
  };

  const onDislikeResponseClicked = async () => {
    if (
      feedbackState === undefined ||
      feedbackState === Feedback.Neutral ||
      feedbackState === Feedback.Positive
    ) {
      setFeedbackState(Feedback.Negative);
      setIsNegativeFeedbackDialogOpen(true);
    } else {
      const newFeedbackState = Feedback.Neutral;
      const newFeedbackText = "";
      setFeedbackState(newFeedbackState);
      setFeedbackText(newFeedbackText);

      try {
        const idToken = await getToken(client);
        if (!idToken) {
          throw new Error("No authentication token available");
        }
        await logUserFeedback(
          Feedback.Neutral,
          newFeedbackText,
          getDecodedContent(),
          context,
          idToken,
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  const updateFeedbackList = (
    ev?: FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean,
  ) => {
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback;
    let feedbackList = negativeFeedbackList.slice();
    if (checked) {
      feedbackList.push(selectedFeedback);
    } else {
      feedbackList = feedbackList.filter((f) => f !== selectedFeedback);
    }
    setNegativeFeedbackList(feedbackList);
  };

  const onSubmitPositiveFeedback = async () => {
    try {
      const idToken = await getToken(client);
      if (!idToken) {
        throw new Error("No authentication token available");
      }
      await logUserFeedback(
        Feedback.Positive,
        feedbackText,
        getDecodedContent(),
        context,
        idToken,
      );
    } catch (error) {
      console.error(error);
    }
    resetPositiveFeedbackDialog();
  };

  const onSubmitNegativeFeedback = async () => {
    try {
      const idToken = await getToken(client);
      if (!idToken) {
        throw new Error("No authentication token available");
      }
      await logUserFeedback(
        negativeFeedbackList.join(","),
        feedbackText,
        getDecodedContent(),
        context,
        idToken,
      );
    } catch (error) {
      console.error(error);
    }
    resetNegativeFeedbackDialog();
  };

  const resetPositiveFeedbackDialog = () => {
    setIsPositiveFeedbackDialogOpen(false);
  };

  const resetNegativeFeedbackDialog = () => {
    setIsNegativeFeedbackDialogOpen(false);
    setNegativeFeedbackList([]);
  };

  const hasThoughts =
    !!answer?.choices?.[0]?.context?.thoughts &&
    Array.isArray(answer.choices[0].context.thoughts) &&
    answer.choices[0].context.thoughts.length > 0;
  const hasDataPoints =
    !!answer?.choices?.[0]?.context?.data_points &&
    Array.isArray(answer.choices[0].context.data_points) &&
    answer.choices[0].context.data_points.length > 0;

  return (
    <div>
      {/* Main answer container */}
      <div
        className={`${styles.answerContainer} ${isSelected && styles.selected}`}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Header row */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <AnswerModeIcon answerMode={answerMode} isLoading={isStreaming} />
            </div>
          </div>
        </div>

        {/* Process steps */}
        {processSteps && processSteps.length > 0 && (
          <div>
            <ProcessStepList steps={processSteps} />
          </div>
        )}

        {/* Markdown content */}
        <MarkdownRenderer
          decodedContent={contentWithViz}
          isStreaming={isStreaming}
        />

        {/* Error block */}
        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorHeader}>
              <ErrorCircle24Regular />
              <span>Generation Stopped</span>
            </div>
            <p className={styles.errorMessage}>{error}</p>
            {onRetry && (
              <button className={styles.retryButtonStyled} onClick={onRetry}>
                <ArrowClockwise24Regular />
                Retry
              </button>
            )}
          </div>
        )}

        {/* Citations */}
        {!!parsedAnswer.citations.length && (
          <div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 5,
                alignItems: "center",
              }}
            >
              <span className={styles.citationLearnMore}>Citations:</span>
              {parsedAnswer.citations.map((x, i) => {
                return (
                  <a
                    key={i}
                    className={styles.citation}
                    title={x}
                    onClick={() => {
                      const path = getCitationFilePath(selectedUseCase, x);
                      onCitationClicked(path);
                    }}
                  >
                    {`${i + 1}. ${x}`}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ACTION BUTTONS */}
      <div className={styles.actionContainer}>
        {/* LIKE */}
        <Tooltip
          content="Like this response"
          positioning="above"
          relationship="label"
        >
          <button
            className={`${styles.actionButton} ${styles.likeButton} ${
              feedbackState === Feedback.Positive ? styles.liked : ""
            }`}
            onClick={() => onLikeResponseClicked()}
            aria-label="Like this response"
          >
            {feedbackState === Feedback.Positive ? (
              <svg
                viewBox="0 0 24 24"
                className={styles.actionIcon}
                fill="currentColor"
              >
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className={styles.actionIcon}>
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
            )}
          </button>
        </Tooltip>

        {/* DISLIKE */}
        <Tooltip
          content="Dislike this response"
          positioning="above"
          relationship="label"
        >
          <button
            className={`${styles.actionButton} ${styles.dislikeButton} ${
              feedbackState !== Feedback.Positive &&
              feedbackState !== Feedback.Neutral &&
              feedbackState !== undefined
                ? styles.disliked
                : ""
            }`}
            onClick={() => onDislikeResponseClicked()}
            aria-label="Dislike this response"
          >
            {feedbackState !== Feedback.Positive &&
            feedbackState !== Feedback.Neutral &&
            feedbackState !== undefined ? (
              <svg
                viewBox="0 0 24 24"
                className={styles.actionIcon}
                fill="currentColor"
              >
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className={styles.actionIcon}>
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
              </svg>
            )}
          </button>
        </Tooltip>

        {/* REGENERATE (Only if last) */}
        {isLast && (
          <Tooltip
            content="Regenerate response"
            positioning="above"
            relationship="label"
          >
            <button
              className={styles.actionButton}
              onClick={onRegenerate}
              disabled={isStreaming}
              aria-label="Regenerate response"
            >
              <svg viewBox="0 0 24 24" className={styles.actionIcon}>
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                <path d="M16 21h5v-5" />
              </svg>
            </button>
          </Tooltip>
        )}

        {/* COPY */}
        <Tooltip
          content={copied ? "Copied" : "Copy"}
          positioning="above"
          relationship="label"
        >
          <button
            className={styles.actionButton}
            onClick={handleCopy}
            aria-label={copied ? "Copied" : "Copy"}
          >
            {copied ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.actionIcon}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.actionIcon}
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
            )}
          </button>
        </Tooltip>

        {/* THOUGHT PROCESS */}
        <Tooltip
          content="Show thought process"
          positioning="above"
          relationship="label"
        >
          <button
            className={styles.actionButton}
            onClick={() => onThoughtProcessClicked()}
            disabled={!hasThoughts}
            aria-label="Show thought process"
          >
            <svg viewBox="0 0 24 24" className={styles.actionIcon}>
              <path d="M12 2a6 6 0 0 1 6 6c0 2.22-1.21 4.16-3 5.2V17a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3.8c-1.79-1.04-3-2.98-3-5.2a6 6 0 0 1 6-6z" />
              <path d="M10 21h4a1 1 0 0 0 1-1v-1H9v1a1 1 0 0 0 1 1z" />
            </svg>
          </button>
        </Tooltip>

        {/* CITATIONS / SUPPORTING CONTENT */}
        <Tooltip
          content="Show supporting content"
          positioning="above"
          relationship="label"
        >
          <button
            className={styles.actionButton}
            onClick={() => onSupportingContentClicked()}
            disabled={!hasDataPoints}
            aria-label="Show supporting content"
          >
            <svg viewBox="0 0 24 24" className={styles.actionIcon}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </button>
        </Tooltip>
      </div>

      {/* POSITIVE FEEDBACK DIALOG */}
      <PositiveFeedbackModal
        open={isPositiveFeedbackDialogOpen}
        onClose={() => {
          resetPositiveFeedbackDialog();
          setFeedbackState(Feedback.Neutral);
          setIsPositiveFeedbackDialogOpen(false);
        }}
        onSubmit={onSubmitPositiveFeedback}
      />

      <NegativeFeedbackModal
        open={isNegativeFeedbackDialogOpen}
        onClose={() => {
          resetNegativeFeedbackDialog();
          setFeedbackState(Feedback.Neutral);
          setIsNegativeFeedbackDialogOpen(false);
        }}
        negativeFeedbackList={negativeFeedbackList}
        updateFeedbackList={updateFeedbackList}
        feedbackText={feedbackText}
        setFeedbackText={setFeedbackText}
        onSubmit={onSubmitNegativeFeedback}
      />
    </div>
  );
};
